import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
import OpenInBrowserRoundedIcon from '@mui/icons-material/OpenInBrowserRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import { getMeCached } from '../authCache';
import { DUY_VE_YAZ_PREVIEW_PATH, isDuyVeYazPreviewEnabled } from '../../mini-tasks/duy-ve-yaz/previewConfig';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import {
  createMiniTaskClassLink,
  deactivateMiniTaskClassLink,
  fetchMiniTaskClassLink,
  rotateMiniTaskClassLink,
} from '../api/miniTasks';

function SetupStep({ number, icon, title, children }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.75,
        minHeight: 132,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            fontWeight: 900,
            flex: '0 0 auto',
          }}
        >
          {number}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ color: 'primary.main', lineHeight: 0, mb: 0.75 }}>
            {icon}
          </Box>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.25 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {children}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function MiniTasksManager() {
  const navigate = useNavigate();
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [linkInfo, setLinkInfo] = useState(null);
  const [freshUrl, setFreshUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const selectedClass = useMemo(
    () => classes.find((item) => String(item.id) === String(selectedClassId)) || null,
    [classes, selectedClassId],
  );

  const shortcutStorageKey = useMemo(() => {
    if (!schoolId || !selectedClassId) return '';
    return `mini_tasks_shortcut_url:${schoolId}:${selectedClassId}`;
  }, [schoolId, selectedClassId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMeCached();
        const sid = me?.data?.admin_user?.school_id;
        if (!sid) throw new Error('Okul bilgisi bulunamadı.');
        const classRows = await fetchClasses(sid);
        if (cancelled) return;
        const list = Array.isArray(classRows) ? classRows : [];
        setSchoolId(sid);
        setClasses(list);
        setSelectedClassId(list[0]?.id ? String(list[0].id) : '');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Mini Görevler yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadLink = async () => {
    if (!schoolId || !selectedClassId) return;
    setError('');
    setMessage('');
    if (shortcutStorageKey) {
      try {
        setFreshUrl(window.localStorage.getItem(shortcutStorageKey) || '');
      } catch (_err) {
        setFreshUrl('');
      }
    } else {
      setFreshUrl('');
    }
    try {
      const payload = await fetchMiniTaskClassLink(schoolId, selectedClassId);
      setLinkInfo(payload.link || null);
    } catch (err) {
      setError(err?.message || 'Link bilgisi alınamadı.');
    }
  };

  useEffect(() => {
    loadLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, selectedClassId]);

  const publicUrlFromPayload = (payload) => {
    const path = payload?.public_path;
    if (!path) return '';
    return `${window.location.origin}${path}`;
  };

  const rememberShortcutUrl = (url) => {
    if (!shortcutStorageKey || !url) return;
    try {
      window.localStorage.setItem(shortcutStorageKey, url);
    } catch (_err) {
      // Download still works for the current screen even if storage is blocked.
    }
  };

  const forgetShortcutUrl = () => {
    if (!shortcutStorageKey) return;
    try {
      window.localStorage.removeItem(shortcutStorageKey);
    } catch (_err) {
      // Ignore storage errors.
    }
  };

  const safeFilePart = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 48) || 'sinif';

  const buildShortcutFile = () => {
    const className = selectedClass?.name || selectedClass?.code || selectedClassId;
    const safeClassName = String(className || 'Sınıf');
    const fileName = `DANDU-Mini-Gorevler-${safeFilePart(className)}.html`;
    const content = `<!doctype html>
<html lang="tr">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta http-equiv="refresh" content="0; url=${freshUrl}">
  <title>DANDU Mini Görevler - ${safeClassName}</title>
  <link rel="icon" href="${window.location.origin}/favicon.ico">
  <style>
    body {
      min-height: 100vh;
      margin: 0;
      display: grid;
      place-items: center;
      font-family: Arial, sans-serif;
      background: #fffaf0;
      color: #242061;
      text-align: center;
    }
    a { color: #5128bc; font-weight: 700; }
  </style>
  <script>window.location.replace(${JSON.stringify(freshUrl)});</script>
</head>
<body>
  <main>
    <h1>DANDU Mini Görevler</h1>
    <p>${safeClassName} açılıyor...</p>
    <p><a href="${freshUrl}">Açılmazsa buraya tıklayın.</a></p>
  </main>
</body>
</html>`;
    const blob = new Blob([content], { type: 'text/html;charset=utf-8' });
    return { blob, fileName };
  };

  const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadShortcut = async () => {
    if (!freshUrl) return;
    const { blob, fileName } = buildShortcutFile();

    if (typeof window.showSaveFilePicker === 'function') {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: 'DANDU Mini Görevler kısayolu',
              accept: { 'text/html': ['.html'] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        setMessage('Kısayol kaydedildi. Konum olarak masaüstünü seçtiyseniz öğrenciler doğrudan oradan açabilir.');
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }

    downloadBlob(blob, fileName);
    setMessage('Kısayol indirildi. Tarayıcı konum seçtirmediyse dosyayı İndirilenler klasöründen masaüstüne taşıyabilirsiniz.');
  };

  const handleCreate = async () => {
    if (!schoolId || !selectedClassId) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = await createMiniTaskClassLink(schoolId, selectedClassId);
      const url = publicUrlFromPayload(payload);
      setLinkInfo(payload.link || null);
      setFreshUrl(url);
      rememberShortcutUrl(url);
      setMessage('Mini Görev linki oluşturuldu.');
    } catch (err) {
      setError(err?.message || 'Link oluşturulamadı.');
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    if (!schoolId || !selectedClassId) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = await rotateMiniTaskClassLink(schoolId, selectedClassId);
      const url = publicUrlFromPayload(payload);
      setLinkInfo(payload.link || null);
      setFreshUrl(url);
      rememberShortcutUrl(url);
      setMessage('Mini Görev linki yenilendi. Eski link artık çalışmaz.');
    } catch (err) {
      setError(err?.message || 'Link yenilenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!schoolId || !selectedClassId) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = await deactivateMiniTaskClassLink(schoolId, selectedClassId);
      setLinkInfo(payload.link || null);
      setFreshUrl('');
      forgetShortcutUrl();
      setMessage('Mini Görev linki pasifleştirildi.');
    } catch (err) {
      setError(err?.message || 'Link pasifleştirilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const copyFreshUrl = async () => {
    if (!freshUrl) return;
    await navigator.clipboard.writeText(freshUrl);
    setMessage('Link panoya kopyalandı.');
  };

  const activeClassName = selectedClass?.name || selectedClass?.code || selectedClassId;
  const hasActiveLink = Boolean(linkInfo?.is_active);
  const canDownloadShortcut = Boolean(freshUrl);
  const statusLabel = hasActiveLink ? 'Aktif link var' : 'Link yok';

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1120 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Mini Görevler</Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            Akıllı tahta sınıf linkini oluşturun ve öğrencilerin masaüstünden açacağı kısayolu hazırlayın.
          </Typography>
        </Box>
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
          {isDuyVeYazPreviewEnabled() && <Button
            variant="outlined"
            startIcon={<RateReviewRoundedIcon />}
            onClick={() => navigate(DUY_VE_YAZ_PREVIEW_PATH)}
            sx={{ borderRadius: 999, fontWeight: 800, minHeight: 44 }}
          >
            Duy ve Yaz — Önizleme
          </Button>}
          <Button
            variant="outlined"
            startIcon={<RateReviewRoundedIcon />}
            onClick={() => navigate('/admin/mini-tasks/content-qa')}
            sx={{ borderRadius: 999, fontWeight: 800 }}
          >
            İçerik Değerlendirme
          </Button>
          <Button
            variant="outlined"
            startIcon={<InsightsRoundedIcon />}
            onClick={() => navigate('/admin/mini-tasks/statistics')}
            sx={{ borderRadius: 999, fontWeight: 800 }}
          >
            İstatistikleri Aç
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          bgcolor: '#fbfcff',
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Akıllı tahta kurulum adımları
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
              Öğretmen linki üretir, kısayol dosyasını indirir ve akıllı tahta masaüstüne yerleştirir.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <SetupStep number="1" icon={<SchoolRoundedIcon fontSize="small" />} title="Sınıfı seç">
              Kısayol hangi sınıf için açılacaksa listeden o sınıfı seçin.
            </SetupStep>
            <SetupStep number="2" icon={<LinkRoundedIcon fontSize="small" />} title="Link oluştur">
              İlk kurulumda link oluşturun. Eski kısayolu iptal etmek için linki yenileyin.
            </SetupStep>
            <SetupStep number="3" icon={<DownloadRoundedIcon fontSize="small" />} title="Kısayolu indir">
              Tarayıcı konum seçtirirse masaüstünü seçin; seçtirmezse dosya İndirilenler klasörüne iner.
            </SetupStep>
            <SetupStep number="4" icon={<DesktopWindowsRoundedIcon fontSize="small" />} title="Tahtada test et">
              Dosyayı akıllı tahta masaüstüne koyun ve bir kez açıp sınıf ana sayfasını kontrol edin.
            </SetupStep>
          </Box>

          <Alert severity="info" icon={<OpenInBrowserRoundedIcon />}>
            Kısayol dosyası HTML formatındadır. Windows ve Pardus üzerinde varsayılan tarayıcıyla açılır.
          </Alert>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 2, md: 2.5 } }}>
        <Stack spacing={2.2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Kısayol üret
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                Sınıf linkini yönetin ve masaüstüne koyulacak kısayolu indirin.
              </Typography>
            </Box>
            <Chip
              icon={hasActiveLink ? <CheckCircleRoundedIcon /> : undefined}
              label={statusLabel}
              color={hasActiveLink ? 'success' : 'default'}
              variant={hasActiveLink ? 'filled' : 'outlined'}
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          <Divider />

          <TextField
            select
            label="Sınıf"
            value={selectedClassId}
            onChange={(event) => setSelectedClassId(event.target.value)}
            size="small"
            sx={{ maxWidth: 360 }}
          >
            {classes.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.name || item.code || item.id}
              </MenuItem>
            ))}
          </TextField>

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.75,
              bgcolor: '#f8fafc',
            }}
          >
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
              {selectedClass ? `${activeClassName} sınıf linki` : 'Sınıf linki'}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Durum: {hasActiveLink ? `aktif (${linkInfo.token_prefix}...)` : 'henüz link yok'}
            </Typography>
            {linkInfo?.expires_at && (
              <Typography sx={{ color: 'text.secondary' }}>
                Geçerlilik: {new Date(linkInfo.expires_at).toLocaleString('tr-TR')}
              </Typography>
            )}
          </Box>

          {freshUrl && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'info.light',
                borderRadius: 2,
                bgcolor: '#eaf7ff',
                p: 1.5,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                    Kısayol linki
                  </Typography>
                  <Typography sx={{ color: '#073b5a', wordBreak: 'break-all' }}>
                    {freshUrl}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                  <Button color="info" variant="text" size="small" startIcon={<ContentCopyRoundedIcon />} onClick={copyFreshUrl}>
                    Kopyala
                  </Button>
                  <Button color="info" variant="text" size="small" startIcon={<DownloadRoundedIcon />} onClick={downloadShortcut}>
                    Kısayol
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {hasActiveLink && !freshUrl && (
            <Alert severity="warning">
              Bu sınıf için aktif link var; ancak tam link güvenlik nedeniyle sadece oluşturma/yenileme anında gösterilir.
              Kısayol indirmek için linki yenileyin.
            </Alert>
          )}

          {!hasActiveLink && (
            <Alert severity="info">
              Sonraki adım: sınıf linkini oluşturun, ardından kısayolu indirip akıllı tahta masaüstüne koyun.
            </Alert>
          )}

          {canDownloadShortcut && (
            <Alert severity="success">
              Kısayol indirilmeye hazır. Konum seçme penceresi açılırsa masaüstünü seçin.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} flexWrap="wrap" useFlexGap>
            {!hasActiveLink && (
              <Button
                variant="contained"
                startIcon={<LinkRoundedIcon />}
                onClick={handleCreate}
                disabled={saving || !selectedClassId}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Link Oluştur
              </Button>
            )}
            {hasActiveLink && canDownloadShortcut && (
              <Button
                variant="contained"
                startIcon={<DownloadRoundedIcon />}
                onClick={downloadShortcut}
                disabled={saving || !freshUrl}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Kısayol İndir
              </Button>
            )}
            {hasActiveLink && !canDownloadShortcut && (
              <Button
                variant="contained"
                startIcon={<RefreshRoundedIcon />}
                onClick={handleRotate}
                disabled={saving || !selectedClassId}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Linki Yenile
              </Button>
            )}
            {canDownloadShortcut && (
              <Button
                variant="outlined"
                startIcon={<ContentCopyRoundedIcon />}
                onClick={copyFreshUrl}
                disabled={saving || !freshUrl}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Linki Kopyala
              </Button>
            )}
            {hasActiveLink && canDownloadShortcut && (
              <Button
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={handleRotate}
                disabled={saving || !selectedClassId}
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Yenile
              </Button>
            )}
            <Button
              color="error"
              variant="outlined"
              startIcon={<LinkOffRoundedIcon />}
              onClick={handleDeactivate}
              disabled={saving || !selectedClassId || !hasActiveLink}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Pasifleştir
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
